/**
 * LexiStar trip cost API client (ST-00188, M4-C; docs/lexistar/contracts/m4-contracts.md §5;
 * LS-04 §11; FR-WB-011; LS-09 §5.5 "estimated cost to transporter"). Server:
 * server/modules/lexistar/costs, mounted at /api/lexistar. Shapes: `types/lexistar-costs`.
 *
 * Thin wrapper over the shared axios instance in `services/api` (cookie-session auth, CSRF
 * header, single-flight 401 refresh; `api` already carries `/api`). Resolves to the response
 * BODY. Cost is ESTIMATED everywhere in the UI, whatever `cost.state` says (`final` only
 * means the record was signed off and the figure is frozen).
 *
 *   getTripCost(tripId)                 → TripCostResponse   (stored: false = preview)
 *   addTripCostLine(tripId, line)       → { cost, line }     (409 code 'signed_off')
 *   recomputeTripCost(tripId)           → TripCostResponse   (409 'signed_off' | 'not_finished')
 *   getCostSummary({from, to, by, …})   → CostSummaryResponse
 */
import api from './api';
import type {
  CostLine,
  CostSummaryBy,
  CostSummaryResponse,
  ManualCostLineInput,
  TripCostResponse,
  TripCostRow,
  TripCostState,
} from '../types/lexistar-costs';

export type {
  CostLine,
  CostSummaryBy,
  CostSummaryResponse,
  ManualCostLineInput,
  TripCostResponse,
  TripCostRow,
  TripCostState,
};

const LX = '/lexistar';

type Params = Record<string, string | number | undefined | null>;

const clean = (params: Params): Record<string, string | number> =>
  Object.fromEntries(
    Object.entries(params).filter(
      (e): e is [string, string | number] => e[1] !== undefined && e[1] !== null && e[1] !== ''
    )
  );

const isoOf = (v: Date | string | undefined): string | undefined =>
  v instanceof Date ? v.toISOString() : v;

const tripPath = (tripId: string): string => `${LX}/trips/${encodeURIComponent(tripId)}/cost`;

/** The trip's cost (stored, else a preview computed now) + "how was this calculated?". */
export async function getTripCost(tripId: string): Promise<TripCostResponse> {
  const res = await api.get(tripPath(tripId));
  return res.data as TripCostResponse;
}

/** Add a manual line (permit, escort, fine, P0 manual toll…) — lexistar.costs.manage. */
export async function addTripCostLine(
  tripId: string,
  line: ManualCostLineInput
): Promise<{ cost: TripCostRow; line: CostLine }> {
  const res = await api.post(`${tripPath(tripId)}/lines`, line);
  return res.data as { cost: TripCostRow; line: CostLine };
}

/** Recompute and store now (not on a signed-off trip) — lexistar.costs.manage. */
export async function recomputeTripCost(
  tripId: string,
  reason?: string
): Promise<TripCostResponse> {
  const res = await api.post(`${tripPath(tripId)}:recompute`, reason ? { reason } : {});
  return res.data as TripCostResponse;
}

export interface CostSummaryQuery {
  from: Date | string;
  to: Date | string;
  by?: CostSummaryBy;
  state?: TripCostState;
  /** Global admins only: one tenant. */
  tenantId?: number;
}

/** Cost totals per route / customer / material / asset / driver over [from, to) (≤ 366 days). */
export async function getCostSummary({
  from,
  to,
  by = 'route',
  state,
  tenantId,
}: CostSummaryQuery): Promise<CostSummaryResponse> {
  const res = await api.get(`${LX}/costs`, {
    params: clean({ from: isoOf(from), to: isoOf(to), by, state, tenantId }),
  });
  return res.data as CostSummaryResponse;
}

const lexistarCosts = { getTripCost, addTripCostLine, recomputeTripCost, getCostSummary };
export default lexistarCosts;
